import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Key } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AccountSettings } from "../components/AccountSettings";

export default function StudySettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/subscribers").then((r) => setSubs(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Ajustes guardados");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!s) return <div className="p-10 text-center text-[#52635C]">Cargando…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10" data-testid="settings-page">
      <Link to="/estudio" className="btn-ghost !min-h-10 mb-6"><ArrowLeft size={16} /> Volver al estudio</Link>
      <div className="eyebrow mb-2">Ajustes</div>
      <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#13302A] mb-8">Datos de la iglesia</h1>

      <AccountSettings />

      <div className="card-warm p-6 space-y-4 mb-6">
        <div>
          <label className="eyebrow block mb-2">Dirección</label>
          <input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} className="input-warm" placeholder="Calle, Nº, Ciudad del Este, Paraguay" data-testid="set-address" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Horarios de culto</label>
          <textarea value={s.service_hours || ""} onChange={(e) => setS({ ...s, service_hours: e.target.value })} className="input-warm min-h-[100px]" placeholder="Domingos 09:00 y 18:30 — Miércoles 19:30" data-testid="set-hours" />
        </div>
        <div>
          <label className="eyebrow block mb-2">Enlace a Google Maps</label>
          <input value={s.maps_url || ""} onChange={(e) => setS({ ...s, maps_url: e.target.value })} className="input-warm" data-testid="set-maps" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow block mb-2">Nombre de la iglesia</label>
            <input value={s.church_name || ""} onChange={(e) => setS({ ...s, church_name: e.target.value })} className="input-warm" data-testid="set-church-name" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Ciudad</label>
            <input value={s.city || ""} onChange={(e) => setS({ ...s, city: e.target.value })} className="input-warm" data-testid="set-city" />
          </div>
        </div>
      </div>

      <div className="card-warm p-6 space-y-3 mb-6">
        <h2 className="font-serif text-xl font-semibold text-[#13302A]">Versículo de la semana</h2>
        <p className="text-sm text-[#52635C]">Aparece como una franja dorada enorme en la portada. Pensalo como el versículo que querés que la congregación memorice esta semana.</p>
        <textarea
          value={s.verse_of_week || ""}
          onChange={(e) => setS({ ...s, verse_of_week: e.target.value })}
          className="input-warm min-h-[90px]"
          placeholder="Fíate de Jehová de todo tu corazón…"
          data-testid="set-verse-text"
        />
        <input
          value={s.verse_of_week_ref || ""}
          onChange={(e) => setS({ ...s, verse_of_week_ref: e.target.value })}
          className="input-warm"
          placeholder="Proverbios 3:5-6"
          data-testid="set-verse-ref"
        />
      </div>

      <div className="card-warm p-6 space-y-3 mb-6">
        <h2 className="font-serif text-xl font-semibold text-[#13302A]">Canal de WhatsApp</h2>
        <p className="text-sm text-[#52635C]">
          Pegá el enlace del canal (ej: <span className="font-mono">https://whatsapp.com/channel/XXXX</span>). Es gratis y no cuesta nada mantenerlo: a diferencia de la API de WhatsApp Business, los canales son abiertos y la gente se une con un toque. Si dejás el campo vacío, la tarjeta se oculta del sitio.
        </p>
        <input
          type="url"
          value={s.whatsapp_channel_url || ""}
          onChange={(e) => setS({ ...s, whatsapp_channel_url: e.target.value })}
          className="input-warm"
          placeholder="https://whatsapp.com/channel/..."
          data-testid="set-wa-channel"
        />
      </div>

      <div className="card-warm p-6 space-y-3 mb-6">
        <h2 className="font-serif text-xl font-semibold text-[#13302A]">URL pública del sitio</h2>
        <p className="text-sm text-[#52635C]">Se usa para armar los enlaces en los correos a tus suscriptores. Debe empezar con https:// y no llevar barra al final.</p>
        <input
          type="url"
          value={s.site_public_url || ""}
          onChange={(e) => setS({ ...s, site_public_url: e.target.value })}
          className="input-warm"
          placeholder="https://www.casadeoracioncde.org"
          data-testid="set-public-url"
        />
      </div>

      <div className="card-warm p-6 space-y-3 mb-6">
        <div className="flex items-center gap-2">
          <Key size={16} className="text-[#1F4A40]" />
          <h2 className="font-serif text-xl font-semibold text-[#13302A]">API.Bible</h2>
        </div>
        <p className="text-sm text-[#52635C]">
          Tu clave está cargada como variable segura del servidor. Versiones habilitadas en tu plan Starter (5.000 consultas/mes, con caché):
          <strong> NBLA, NTV, NVI, NVIC, RVR09, VBL, PDDPT, Biblia en español sencillo</strong>. La Reina-Valera 1909 queda como respaldo offline (dominio público).
        </p>
      </div>

      <div className="flex justify-end mb-10">
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="btn-save-settings"><Save size={16} /> {saving ? "Guardando…" : "Guardar ajustes"}</button>
      </div>

      <div className="card-warm p-6">
        <h2 className="font-serif text-xl font-semibold text-[#13302A] mb-3">Suscriptores</h2>
        {subs.length === 0 ? (
          <p className="text-sm text-[#52635C] italic">Aún no hay suscripciones.</p>
        ) : (
          <ul className="divide-y divide-[rgba(31,74,64,0.1)]">
            {subs.map((sub) => (
              <li key={sub.id} className="py-2 flex items-center justify-between text-sm">
                <span className="badge-soft">{sub.kind === "email" ? "Correo" : "WhatsApp"}</span>
                <span className="font-mono flex-1 ml-3">{sub.value}</span>
                <span className="text-[#52635C] text-xs">{new Date(sub.created_at).toLocaleDateString("es-PY")}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
